import {
  ORIENTATIONS,
  PRIORITY_CLASSES,
  SCALING_MODES,
  type Manifest,
  type ManifestItem,
  type ManifestSettings,
  type Orientation,
  type PriorityClass,
  type QuietHours,
  type ScalingMode,
} from './types.js';

export type ParseResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly errors: readonly string[] };

/** A loop item this player could not read, dropped from the loop instead of failing the manifest. */
export interface SkippedItem {
  readonly index: number;
  /** The item's own id when that much was readable, so the skip can be traced to a compile. */
  readonly item_id: string | null;
  readonly errors: readonly string[];
}

export type ManifestParseResult =
  | {
      readonly ok: true;
      readonly value: Manifest;
      /** Items dropped from the loop because this player cannot read them. */
      readonly skipped: readonly SkippedItem[];
      /** Presentation fields this player did not recognise and replaced with its own default. */
      readonly defaulted: readonly string[];
    }
  | { readonly ok: false; readonly errors: readonly string[] };

const SHA256_HEX = /^[0-9a-f]{64}$/;
const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Validate an untrusted manifest body. A rejected manifest is not adopted: the player
 * keeps the last known-good loop (technical-design §11.7), so this returns errors
 * rather than throwing.
 *
 * Failure is scoped to the smallest thing that cannot be read (decision-log O198). A
 * fleet always runs more than one bundle version, so a value an older bundle does not
 * recognise must not freeze every screen running it on its last loop:
 *
 * - **The envelope rejects the manifest**: the top-level fields, the loop not being an
 *   array, the required settings, and the two settings whose fallback would change what
 *   plays or when. A blocklist dropped to empty would undo a takedown (design-addendum
 *   §2), and quiet hours dropped to none would play into closed hours.
 * - **An item is skipped**: one the player cannot read, including a `priority_class` it
 *   does not know, is left out of the loop and the rest is adopted. A retired class is
 *   therefore still never played, which is all rejecting the manifest was protecting.
 * - **A presentation field falls back**: an unrecognised `scaling_mode`,
 *   `background_color`, `audio_enabled` or `screen_orientation` is replaced by the
 *   player's default (fit, none, muted, detect), and the item still plays.
 *
 * A loop the server sent empty means house content (§11.7). A loop with items of which
 * none is readable is rejected instead: keeping the last known-good loop is the better
 * outcome when the cause is a compiler fault, and house content would replace every paid
 * item on every screen that hit it.
 */
export function parseManifest(input: unknown): ManifestParseResult {
  const errors: string[] = [];
  const root = asRecord(input, '$', errors);
  if (!root) return { ok: false, errors };

  const defaulted: string[] = [];
  const manifestVersion = int(root['manifest_version'], '$.manifest_version', errors, { min: 0 });
  const screenId = str(root['screen_id'], '$.screen_id', errors);
  const issuedAt = isoTimestamp(root['issued_at'], '$.issued_at', errors);
  const pollInterval = int(root['poll_interval_seconds'], '$.poll_interval_seconds', errors, {
    min: 1,
  });
  const settings = parseSettings(root['settings'], errors, defaulted);

  const loopRaw = root['loop'];
  const loop: ManifestItem[] = [];
  const skipped: SkippedItem[] = [];
  if (!Array.isArray(loopRaw)) {
    errors.push('$.loop: expected an array');
  } else {
    loopRaw.forEach((raw, index) => {
      const itemErrors: string[] = [];
      const itemDefaulted: string[] = [];
      const item = parseItem(raw, `$.loop[${index}]`, itemErrors, itemDefaulted);
      if (item) {
        loop.push(item);
        defaulted.push(...itemDefaulted);
      } else {
        skipped.push({ index, item_id: readableItemId(raw), errors: itemErrors });
      }
    });
    if (loopRaw.length > 0 && loop.length === 0) {
      errors.push(...skipped.flatMap((entry) => entry.errors));
    }
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    value: {
      manifest_version: manifestVersion as number,
      screen_id: screenId as string,
      issued_at: issuedAt as string,
      poll_interval_seconds: pollInterval as number,
      loop,
      settings: settings as ManifestSettings,
    },
    skipped,
    defaulted,
  };
}

function readableItemId(input: unknown): string | null {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) return null;
  const id = (input as Record<string, unknown>)['item_id'];
  return typeof id === 'string' && id.length > 0 ? id : null;
}

function parseItem(
  input: unknown,
  path: string,
  errors: string[],
  defaulted: string[],
): ManifestItem | null {
  const record = asRecord(input, path, errors);
  if (!record) return null;

  const itemId = str(record['item_id'], `${path}.item_id`, errors);
  const campaignId = str(record['campaign_id'], `${path}.campaign_id`, errors);
  const creativeId = str(record['creative_id'], `${path}.creative_id`, errors);
  const venueId = str(record['venue_id'], `${path}.venue_id`, errors);
  const assetUrl = str(record['asset_url'], `${path}.asset_url`, errors);
  const mediaType = str(record['media_type'], `${path}.media_type`, errors);
  const durationMs = int(record['duration_ms'], `${path}.duration_ms`, errors, { min: 1 });
  const priorityClass = oneOf<PriorityClass>(
    record['priority_class'],
    PRIORITY_CLASSES,
    `${path}.priority_class`,
    errors,
  );

  const sha = str(record['asset_sha256'], `${path}.asset_sha256`, errors);
  if (sha !== null && !SHA256_HEX.test(sha)) {
    errors.push(`${path}.asset_sha256: expected 64 lowercase hex characters`);
  }

  const endsAt = record['ends_at'] == null ? null : isoTimestamp(record['ends_at'], `${path}.ends_at`, errors);

  // `errors` is this item's own list, so anything in it means the item is skipped.
  if (errors.length > 0) return null;

  const scaling = presentationOneOf<ScalingMode>(
    record['scaling_mode'],
    SCALING_MODES,
    `${path}.scaling_mode`,
    defaulted,
  );
  const background = presentationString(record['background_color'], `${path}.background_color`, defaulted);
  const audioEnabled = presentationBoolean(record['audio_enabled'], `${path}.audio_enabled`, defaulted);

  return {
    item_id: itemId as string,
    campaign_id: campaignId as string,
    creative_id: creativeId as string,
    venue_id: venueId as string,
    asset_url: assetUrl as string,
    asset_sha256: sha as string,
    media_type: mediaType as string,
    duration_ms: durationMs as number,
    priority_class: priorityClass as PriorityClass,
    ends_at: endsAt,
    ...(scaling === undefined ? {} : { scaling_mode: scaling }),
    ...(background === undefined ? {} : { background_color: background }),
    ...(audioEnabled === undefined ? {} : { audio_enabled: audioEnabled }),
  };
}

function parseSettings(input: unknown, errors: string[], defaulted: string[]): ManifestSettings | null {
  const record = asRecord(input, '$.settings', errors);
  if (!record) return null;

  const heartbeat = int(record['heartbeat_interval_seconds'], '$.settings.heartbeat_interval_seconds', errors, { min: 1 });
  const batch = int(record['event_batch_size'], '$.settings.event_batch_size', errors, { min: 1 });
  const cacheMax = int(record['cache_max_bytes'], '$.settings.cache_max_bytes', errors, { min: 1 });

  let quietHours: QuietHours | null = null;
  const quietRaw = record['quiet_hours'];
  if (quietRaw != null) {
    const quiet = asRecord(quietRaw, '$.settings.quiet_hours', errors);
    if (quiet) {
      const start = str(quiet['start'], '$.settings.quiet_hours.start', errors);
      const end = str(quiet['end'], '$.settings.quiet_hours.end', errors);
      for (const [value, path] of [
        [start, '$.settings.quiet_hours.start'],
        [end, '$.settings.quiet_hours.end'],
      ] as const) {
        if (value !== null && !HH_MM.test(value)) errors.push(`${path}: expected HH:MM`);
      }
      if (start !== null && end !== null && HH_MM.test(start) && HH_MM.test(end)) {
        quietHours = { start, end };
      }
    }
  }

  let blocklist: readonly string[] | undefined;
  const blocklistRaw = record['blocklist'];
  if (blocklistRaw !== undefined && blocklistRaw !== null) {
    if (!Array.isArray(blocklistRaw) || blocklistRaw.some((entry) => typeof entry !== 'string')) {
      errors.push('$.settings.blocklist: expected an array of creative ids');
    } else {
      blocklist = blocklistRaw as string[];
    }
  }

  const orientation = presentationOneOf<Orientation>(
    record['screen_orientation'],
    ORIENTATIONS,
    '$.settings.screen_orientation',
    defaulted,
  );

  if (heartbeat === null || batch === null || cacheMax === null) return null;

  return {
    heartbeat_interval_seconds: heartbeat,
    event_batch_size: batch,
    cache_max_bytes: cacheMax,
    quiet_hours: quietHours,
    ...(blocklist === undefined ? {} : { blocklist }),
    ...(orientation === undefined ? {} : { screen_orientation: orientation }),
  };
}

function asRecord(input: unknown, path: string, errors: string[]): Record<string, unknown> | null {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    errors.push(`${path}: expected an object`);
    return null;
  }
  return input as Record<string, unknown>;
}

function str(input: unknown, path: string, errors: string[]): string | null {
  if (typeof input !== 'string' || input.length === 0) {
    errors.push(`${path}: expected a non-empty string`);
    return null;
  }
  return input;
}

/*
 * Presentation fields. Absent or null is the default with nothing to report; a value this
 * player does not recognise is the default too, and the path is recorded. Only for fields
 * whose default is safe to put on a screen — never for one that decides what plays or when.
 */

function presentationOneOf<T extends string>(
  input: unknown,
  allowed: readonly T[],
  path: string,
  defaulted: string[],
): T | undefined {
  if (input === undefined || input === null) return undefined;
  if (typeof input === 'string' && allowed.includes(input as T)) return input as T;
  defaulted.push(`${path}: not one of ${allowed.join(', ')}; using the default`);
  return undefined;
}

function presentationString(input: unknown, path: string, defaulted: string[]): string | undefined {
  if (input === undefined || input === null) return undefined;
  if (typeof input === 'string' && input.length > 0) return input;
  defaulted.push(`${path}: expected a non-empty string; using the default`);
  return undefined;
}

function presentationBoolean(input: unknown, path: string, defaulted: string[]): boolean | undefined {
  if (input === undefined || input === null) return undefined;
  if (typeof input === 'boolean') return input;
  defaulted.push(`${path}: expected a boolean; using the default`);
  return undefined;
}

function int(input: unknown, path: string, errors: string[], bounds: { min: number }): number | null {
  if (typeof input !== 'number' || !Number.isInteger(input) || input < bounds.min) {
    errors.push(`${path}: expected an integer >= ${bounds.min}`);
    return null;
  }
  return input;
}

function isoTimestamp(input: unknown, path: string, errors: string[]): string | null {
  const value = str(input, path, errors);
  if (value === null) return null;
  if (Number.isNaN(Date.parse(value))) {
    errors.push(`${path}: expected an ISO-8601 UTC timestamp`);
    return null;
  }
  return value;
}

function oneOf<T extends string>(
  input: unknown,
  allowed: readonly T[],
  path: string,
  errors: string[],
): T | null {
  if (typeof input !== 'string' || !allowed.includes(input as T)) {
    errors.push(`${path}: expected one of ${allowed.join(', ')}`);
    return null;
  }
  return input as T;
}
